import { ProposalState } from "@solana/spl-governance";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { FC, useCallback, useMemo, useState } from "react";
import {
  EMPTY_STRING,
  MESSAGE_TYPE,
} from "../../../../../../common/constants/common.constants";
import { OFFER } from "../../../../../../common/constants/routes.constants";
import { ClubAction } from "../../../../../../common/enums/common.enums";
import arrowDown from "../../../../../../assets/arrowBottom.svg";
import arrowUp from "../../../../../../assets/arrowUp.svg";
import {
  CancellationType,
  OfferStatus,
  ProposalAction,
  ProposalTypeForProgram,
  VoteAction,
} from "../../../../../../common/enums/proposals.enums";
import {
  IBasicProposalData,
  IProposalWithMetaData,
} from "../../../../../../common/interfaces/proposals.interface";
import { REACT_APP_FRONT_BASE_URL } from "../../../../../../solana/common/utilities";
import {
  copyItemToClipboard,
  toggleModalOpenClass,
} from "../../../../../../utilities/helpers";
import "./ProposalsItemActions.scss";
import { IClubDataForProposal } from "../../../../../../common/interfaces/clubs.interface";
import SubmitButton from "../../../../../SubmitButton/SubmitButton";
import {
  cancelEscrow,
  cancelProposal,
  voteToProposal,
} from "../../../../../../solana/methods/proposals";
import { createNotification } from "../../../../../../utilities/notification";
import {
  getClubVault,
  getTreasuryForClub,
} from "../../../../../../solana/methods/clubs";
import ClaimButton from "./ClaimButton/ClaimButton";
import ExecuteButton from "./ExecuteButton/ExecuteButton";
import { distributeProfitAfterMarketplaceSale } from "../../../../../../solana/common/helpers/magic-eden.helpers";

import CancellationModal from "../CancellationModal/CancellationModal";
import { checkIfUserHasPermissionForAction } from "../../../../../../solana/common/helpers/common.helpers";
import { ClubType } from "../../../../../../common/enums/club.enum";
import { PublicKey } from "@solana/web3.js";
import { cancelSolseaProposal } from "../../../../../../solana/common/helpers/solsea.helpers";
import { getNftDataFromSolsea } from "../../../../../../api/solsea.api";
import { clubsStore } from "../../../../../../contexts/clubStore";

interface IProposalsItemActions {
  isDetailsVisible: boolean;
  proposalWithMetaData: IProposalWithMetaData;
  clubBasicInfo: IClubDataForProposal;
  updateProposalWithMetaData: (proposal: IBasicProposalData) => void;
  toggleIsDetailsVisible: (state: boolean) => void;
}

const ProposalsItemActions: FC<IProposalsItemActions> = ({
  isDetailsVisible,
  proposalWithMetaData,
  clubBasicInfo,
  updateProposalWithMetaData,
  toggleIsDetailsVisible,
}) => {
  const wallet = useAnchorWallet();

  const [submitting, toggleSubmitting] = useState(false);
  const [isCancellationModalVisible, toggleIsCancellationModalVisible] =
    useState(false);

  const { setTreasury, setVault, membersNfts } = clubsStore();

  const shouldShowClaimActions = useMemo(() => {
    return (
      !!proposalWithMetaData &&
      proposalWithMetaData.metadata &&
      proposalWithMetaData.metadata.proposalAction ===
        ProposalAction.Withdrawal &&
      proposalWithMetaData.proposalBasicData.proposalAccount.account.state ===
        ProposalState.Completed &&
      proposalWithMetaData.metadata.withdrawalMembersInfo &&
      !proposalWithMetaData.metadata.withdrawalMembersInfo.hasUserClaimed &&
      proposalWithMetaData.metadata.withdrawalMembersInfo.withdrawAmount > 0
    );
  }, [proposalWithMetaData]);

  const hasUserClaimedTokens = useMemo(
    () =>
      !!proposalWithMetaData &&
      proposalWithMetaData.metadata.withdrawalMembersInfo &&
      proposalWithMetaData.metadata.withdrawalMembersInfo.hasUserClaimed,
    [proposalWithMetaData]
  );

  const copyOfferAddress = async () => {
    await copyItemToClipboard(
      `${REACT_APP_FRONT_BASE_URL}${OFFER}/${proposalWithMetaData.proposalBasicData!.proposalAccount.pubkey.toString()}/${
        clubBasicInfo.address
      }`,
      "Offer url copied!"
    );
  };

  const cancelProposalInVotingState = async (
    e,
    reasonId: number,
    customReasonText?: string
  ) => {
    try {
      toggleSubmitting(true);
      if (!proposalWithMetaData!.metadata.canUserCancel) {
        await voteToProposal(
          proposalWithMetaData,
          clubBasicInfo.clubName,
          wallet!,
          clubBasicInfo.treasury,
          VoteAction.Deny,
          clubBasicInfo.clubType,
          clubBasicInfo.mint,
          true,
          {
            reasonId,
            signedTransaction: EMPTY_STRING,
            signerAddress: wallet!.publicKey.toString(),
            customReason: customReasonText,
            proposalAddress:
              proposalWithMetaData.proposalBasicData.proposalAccount.pubkey.toString(),
          }
        );
      } else {
        await cancelProposal(
          clubBasicInfo.clubName,
          proposalWithMetaData!,
          wallet!,
          {
            proposalAddress:
              proposalWithMetaData.proposalBasicData.proposalAccount.pubkey.toString(),
            reasonId,
            signedTransaction: EMPTY_STRING,
            signerAddress: wallet!.publicKey.toString(),
            customReason: customReasonText,
          }
        );
      }
      updateProposalWithMetaData!(proposalWithMetaData.proposalBasicData);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Problem with canceling proposal");
    } finally {
      toggleSubmitting(false);
    }
  };

  const updateTreasuryAfterAction = useCallback(async () => {
    if (!clubBasicInfo.isDashboardProposal) {
      const updatedTreasury = await getTreasuryForClub(clubBasicInfo.clubName);
      setTreasury({ ...updatedTreasury });
    }
  }, [clubBasicInfo]);

  const cancelEscrowHandler = useCallback(
    async (e, reasonId: number, cancellationReason?: string) => {
      try {
        toggleSubmitting(true);

        await cancelEscrow(
          clubBasicInfo.clubName,
          wallet!,
          proposalWithMetaData.metadata.proposalAction,
          proposalWithMetaData.metadata.offer,
          proposalWithMetaData.metadata.makerOfferedTokenAccount!,
          proposalWithMetaData.metadata.maker,
          proposalWithMetaData.proposalBasicData.proposalAccount.pubkey,
          clubBasicInfo.treasury!,
          proposalWithMetaData.metadata.escrowedOfferedTokensAccountPda!,
          {
            reasonId,
            signedTransaction: EMPTY_STRING,
            signerAddress: wallet!.publicKey.toString(),
            customReason: cancellationReason,
            proposalAddress:
              proposalWithMetaData.proposalBasicData.proposalAccount.pubkey.toString(),
          }
        );
        if (!clubBasicInfo.isDashboardProposal) {
          const updatedTreasury = await getTreasuryForClub(
            clubBasicInfo.clubName
          );
          setTreasury(updatedTreasury);
        }
      } catch (error) {
        console.log(error);
      } finally {
        toggleSubmitting(false);
        updateProposalWithMetaData(proposalWithMetaData.proposalBasicData);
      }
    },
    [proposalWithMetaData, wallet, clubBasicInfo.treasury]
  );

  const distributeProfitFromSellProposal = useCallback(async () => {
    try {
      toggleSubmitting(true);
      await distributeProfitAfterMarketplaceSale(
        proposalWithMetaData,
        clubBasicInfo.clubName,
        wallet!,
        clubBasicInfo.treasury
      );
      await updateTreasuryAfterAction();
      createNotification(
        MESSAGE_TYPE.SUCCESS,
        "Funds successfully distributed!"
      );
    } catch (error: any) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Failed to distribute profit",
        error.message
      );
    } finally {
      toggleSubmitting(false);
      updateProposalWithMetaData(proposalWithMetaData.proposalBasicData);
    }
  }, [proposalWithMetaData, clubBasicInfo, wallet]);

  const showCancelButton = useMemo(() => {
    return (
      proposalWithMetaData &&
      proposalWithMetaData.proposalBasicData.proposalAccount.account.state ===
        ProposalState.Voting &&
      !proposalWithMetaData.metadata.isMagicEdenProposal &&
      ((clubBasicInfo.clubType !== ClubType.ProjectFundraise &&
        clubBasicInfo.owner === wallet?.publicKey.toBase58()) ||
        proposalWithMetaData.metadata.canUserCancel)
    );
  }, [proposalWithMetaData, clubBasicInfo, wallet]);

  const canMemberExecuteWithdrawalProposal = useMemo(() => {
    return (
      proposalWithMetaData &&
      proposalWithMetaData.metadata.proposalAction ===
        ProposalAction.Withdrawal &&
      proposalWithMetaData.proposalBasicData!.proposalAccount.account.state ===
        ProposalState.Succeeded
    );
  }, [proposalWithMetaData, clubBasicInfo.userRole]);

  const showSolseaCancelButton = useMemo(() => {
    return (
      proposalWithMetaData.proposalBasicData.proposalAccount.account.state ===
        ProposalState.Completed &&
      proposalWithMetaData.metadata.proposalAction === ProposalAction.SellNft &&
      proposalWithMetaData.metadata.offerStatus ===
        OfferStatus.listedOnSolsea &&
      !proposalWithMetaData.metadata.hasNftSoldOnSolsea
    );
  }, [proposalWithMetaData]);

  const showExecuteButton = useMemo(() => {
    return (
      proposalWithMetaData &&
      proposalWithMetaData.metadata.proposalAction !==
        ProposalAction.Discussion &&
      ((proposalWithMetaData.proposalBasicData!.proposalAccount.account
        .state === ProposalState.Succeeded &&
        proposalWithMetaData.metadata.proposalAction !==
          ProposalAction.Withdrawal) ||
        (proposalWithMetaData.metadata.isMagicEdenProposal &&
          proposalWithMetaData.proposalBasicData.proposalAccount.account
            .state === ProposalState.Succeeded) ||
        proposalWithMetaData.metadata?.offerStatus ===
          OfferStatus.pendingBuyNowExecutionOnMagicEden ||
        proposalWithMetaData.metadata.offerStatus ===
          OfferStatus.pendingSellExecutionOnMagicEden ||
        canMemberExecuteWithdrawalProposal ||
        proposalWithMetaData.metadata.offerStatus ===
          OfferStatus.executedBuyNowOnSolsea)
    );
  }, [proposalWithMetaData, canMemberExecuteWithdrawalProposal]);

  const showOfferButtons = useMemo(() => {
    return (
      proposalWithMetaData &&
      (proposalWithMetaData.metadata.proposalAction === ProposalAction.BuyNft ||
        proposalWithMetaData.metadata.proposalAction ===
          ProposalAction.SellNft) &&
      !proposalWithMetaData.metadata.isMagicEdenProposal &&
      proposalWithMetaData.proposalBasicData.proposalAccount.account.state ===
        ProposalState.Completed &&
      proposalWithMetaData.metadata.offerStatus === OfferStatus.initialised
    );
  }, [proposalWithMetaData]);

  const showDistributeButton = useMemo(() => {
    return (
      proposalWithMetaData.metadata.offerStatus ===
        OfferStatus.listedOnSolsea &&
      proposalWithMetaData.metadata.hasNftSoldOnSolsea
    );
  }, [proposalWithMetaData]);

  const cancelSolseaOffer = useCallback(
    async (e, reasonId: number, cancellationReason?: string) => {
      try {
        const solseaListingData = await getNftDataFromSolsea(
          proposalWithMetaData.metadata.offeredTokenMint.toString()
        );
        if (!solseaListingData || !solseaListingData.listed)
          throw new Error("NFT sold on Solsea");
        await cancelSolseaProposal(
          clubBasicInfo.clubName,
          proposalWithMetaData.proposalBasicData.proposalAccount.pubkey,
          proposalWithMetaData.metadata.offer,
          new PublicKey(solseaListingData.escrowKey),
          proposalWithMetaData.metadata.offeredTokenMint,
          wallet!,
          clubBasicInfo.treasury,
          {
            reasonId,
            signedTransaction: EMPTY_STRING,
            signerAddress: wallet!.publicKey.toString(),
            customReason: cancellationReason,
            proposalAddress:
              proposalWithMetaData.proposalBasicData.proposalAccount.pubkey.toString(),
          }
        );
        updateProposalWithMetaData(proposalWithMetaData.proposalBasicData);
        if (!clubBasicInfo.isDashboardProposal) {
          const updatedVault = await getClubVault(clubBasicInfo.clubName);
          setVault({
            nfts: [...updatedVault.nfts],
            vaultAddress: updatedVault.vaultAddress,
          });
        }

        createNotification(
          MESSAGE_TYPE.SUCCESS,
          "NFT successfully unlisted from SolSea"
        );
      } catch (error: any) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Failed to unlist NFT",
          error.message
        );
      }
    },
    [proposalWithMetaData, clubBasicInfo, wallet]
  );

  const getCancellationTypeBasedOnProposalAction = useMemo(() => {
    switch (proposalWithMetaData.metadata.proposalAction) {
      case ProposalAction.Discussion:
        return CancellationType.DISCUSSION;
      case ProposalAction.Withdrawal:
        return CancellationType.WITHDRAWAL;
      case ProposalAction.BuyNft:
        return proposalWithMetaData.proposalBasicData.proposalMetadata
          .proposalType === ProposalTypeForProgram.BuySolsea
          ? CancellationType.BUY_SOLSEA
          : proposalWithMetaData.proposalBasicData.proposalMetadata
              .proposalType === ProposalTypeForProgram.BuyNowMagicEden
          ? CancellationType.BUY_ME
          : CancellationType.BUY_P2P;
      case ProposalAction.SellNft:
        return proposalWithMetaData.proposalBasicData.proposalMetadata
          .proposalType === ProposalTypeForProgram.SellSolsea
          ? CancellationType.SELL_SOLSEA
          : CancellationType.SELL_P2P;
      default:
        return CancellationType.DISCUSSION;
    }
  }, [proposalWithMetaData]);

  const getCancellationFunction = useMemo(() => {
    if (
      proposalWithMetaData.proposalBasicData.proposalAccount.account.state ===
      ProposalState.Voting
    )
      return cancelProposalInVotingState;
    switch (proposalWithMetaData.metadata.proposalAction) {
      case ProposalAction.Discussion:
      case ProposalAction.Withdrawal:
        return cancelProposalInVotingState;
      case ProposalAction.BuyNft:
        return cancelEscrowHandler;
      case ProposalAction.SellNft:
        return proposalWithMetaData.proposalBasicData.proposalMetadata
          .proposalType === ProposalTypeForProgram.SellSolsea
          ? cancelSolseaOffer
          : cancelEscrowHandler;
      default:
        return cancelProposalInVotingState;
    }
  }, [proposalWithMetaData]);

  const shouldBeAbleToCancelTrade = useMemo(() => {
    return (
      !!clubBasicInfo.userRole &&
      checkIfUserHasPermissionForAction(
        clubBasicInfo.userRole,
        clubBasicInfo.roleConfig,
        ClubAction.CancelP2POffer
      )
    );
  }, [clubBasicInfo]);

  return (
    <>
      {isCancellationModalVisible && (
        <CancellationModal
          closeModal={() => toggleIsCancellationModalVisible(false)}
          submitFunction={getCancellationFunction}
          cancellationType={getCancellationTypeBasedOnProposalAction}
        />
      )}
      <div className="proposals-item-actions">
        <div className="proposals-item-actions__details">
          <button
            className="proposals-item-actions__details-button"
            onClick={() => {
              toggleIsDetailsVisible(!isDetailsVisible);
            }}
          >
            {isDetailsVisible ? "Hide details" : "Show details"}
            <img
              src={!isDetailsVisible ? arrowDown : arrowUp}
              alt="arrowDownIcon"
              className="proposals-item__arrow"
            />
          </button>
          <div className="proposals-item-actions__separator"></div>
        </div>
        {(showCancelButton || showExecuteButton) && (
          <div className="proposals-item-actions__form-buttons">
            {showCancelButton && (
              <button
                className="proposals-item-actions__form-button proposals-item-actions__form-button--cancel"
                onClick={() => {
                  toggleIsCancellationModalVisible(true);
                  toggleModalOpenClass(false);
                }}
              >
                Cancel proposal
              </button>
            )}
            {showExecuteButton && (
              <ExecuteButton
                clubBasicInfoProposal={clubBasicInfo}
                proposalWithMetaData={proposalWithMetaData}
                updateProposalWithMetaData={updateProposalWithMetaData}
              />
            )}
          </div>
        )}

        {showOfferButtons && (
          <div className="proposals-item-actions__form-buttons">
            {shouldBeAbleToCancelTrade && (
              <button
                className="proposals-item-actions__form-button proposals-item-actions__form-button--cancel"
                onClick={() => {
                  toggleIsCancellationModalVisible(true);
                }}
              >
                Cancel trade
              </button>
            )}
            <button
              className="proposals-item-actions__form-button"
              onClick={copyOfferAddress}
            >
              Copy Trade URL
            </button>
          </div>
        )}

        {showSolseaCancelButton && (
          <button
            className="proposals-item-actions__form-button proposals-item-actions__form-button--cancel"
            onClick={() => {
              toggleIsCancellationModalVisible(true);
              toggleModalOpenClass(false);
            }}
          >
            Cancel offer
          </button>
        )}

        {shouldShowClaimActions && !hasUserClaimedTokens && (
          <ClaimButton
            clubBasicInfo={clubBasicInfo}
            proposalWithMetaData={proposalWithMetaData}
          />
        )}
        {showDistributeButton && (
          <SubmitButton
            className="proposals-item-actions__form-button"
            onClick={distributeProfitFromSellProposal}
            submitting={submitting}
          >
            Distribute
          </SubmitButton>
        )}
      </div>
      {hasUserClaimedTokens && (
        <div className="proposals-item-actions__claimed">
          <p>Withdrawal claimed</p>
        </div>
      )}
      {proposalWithMetaData.metadata.cancellationReason && (
        <div className="proposals-item-actions__cancellation-reason">
          <p>
            Cancel reason: {proposalWithMetaData.metadata.cancellationReason}
          </p>
        </div>
      )}
    </>
  );
};

export default ProposalsItemActions;
